import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAuthStore,
  type User,
  type LanguageCode,
  type ImpersonatorInfo,
  type ImpersonateUserResponse,
  type EndImpersonationResponse,
} from "@/store/auth";
import api from "@/services/api";
import { initializeLanguage } from "@/i18n";
import { useSidebarStore, type SidebarStyle } from "@/store/sidebar";

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  mfaRequired?: boolean;
  mfaToken?: string | null;
  forcedAction?: string | null;
  mfaEnforcementWarning?: boolean;
  mfaEnforcementDeadline?: string | null;
}

interface SwitchCompanyResponse {
  accessToken: string;
  refreshToken: string;
  permissions: string[];
}

interface ProfileUpdateData {
  email?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string | null;
  namePrefix?: string | null;
  pronouns?: string | null;
  preferredLanguage?: LanguageCode | null;
  phone?: string | null;
  documentNumber?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  timezone?: string | null;
  sidebarStyle?: string | null;
}

interface ProfileUpdateResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  namePrefix: string | null;
  pronouns: string | null;
  preferredLanguage: LanguageCode | null;
  phone: string | null;
  documentNumber: string | null;
  jobTitle: string | null;
  department: string | null;
  timezone: string | null;
  sidebarStyle: string | null;
}

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  currentCompanyId: string | null;
  permissions: string[];
  isImpersonating: boolean;
  impersonator: ImpersonatorInfo | null;
  forcedAction: string | null;
  mfaEnforcementWarning: boolean;
  mfaEnforcementDeadline: string | null;
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  updateLanguage: (language: LanguageCode | null) => Promise<void>;
  updateProfile: (data: ProfileUpdateData) => Promise<void>;
  startImpersonation: (userId: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  clearForcedAction: () => void;
  dismissMfaEnforcement: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

function syncSidebarFromUser(user: User | null) {
  if (user?.sidebarStyle) {
    const valid: SidebarStyle[] = ["expanded", "collapsed", "topbar", "compact"];
    if (valid.includes(user.sidebarStyle as SidebarStyle)) {
      useSidebarStore.getState().setSidebarStyle(user.sidebarStyle as SidebarStyle);
    }
  }
}

export function useAuth(): UseAuthReturn {
  const queryClient = useQueryClient();
  const {
    user,
    isAuthenticated,
    isLoading,
    isImpersonating,
    impersonator,
    forcedAction,
    mfaEnforcementWarning,
    mfaEnforcementDeadline,
    setUser,
    setTokens,
    setCurrentCompany,
    setPermissions,
    setPreferredLanguage,
    setLoading,
    setForcedAction,
    setMfaEnforcement,
    logout: storeLogout,
    startImpersonation: storeStartImpersonation,
    endImpersonation: storeEndImpersonation,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuthStore();

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { accessToken } = useAuthStore.getState();

      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        // Verify token by fetching current user
        const userData = await api.get<User>("/v1/auth/me");
        setUser(userData);
        syncSidebarFromUser(userData);
        // Initialize language based on user and company preferences
        initializeLanguage(userData.preferredLanguage, userData.companyDefaultLanguage);
      } catch {
        // Token invalid - clear auth state
        storeLogout();
      }
    };

    checkAuth();
  }, [setLoading, setUser, storeLogout]);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResponse> => {
      const response = await api.post<LoginResponse>("/v1/auth/login", credentials);

      // Check if MFA is required
      if (response.mfaRequired && response.mfaToken) {
        // Throw an object with MFA info so the LoginPage can handle it
        throw { mfaRequired: true, mfaToken: response.mfaToken };
      }

      if (!response.accessToken || !response.refreshToken || !response.user) {
        throw new Error("Invalid login response");
      }

      setTokens(response.accessToken, response.refreshToken);
      setUser(response.user);
      syncSidebarFromUser(response.user);

      // Store forced action / MFA enforcement info
      if (response.forcedAction) {
        setForcedAction(response.forcedAction);
      }
      if (response.mfaEnforcementWarning) {
        setMfaEnforcement(true, response.mfaEnforcementDeadline ?? null);
      }

      // Initialize language based on user and company preferences
      initializeLanguage(response.user.preferredLanguage, response.user.companyDefaultLanguage);

      return response;
    },
    [setTokens, setUser, setForcedAction, setMfaEnforcement],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/v1/auth/logout");
    } catch {
      // Ignore logout errors
    } finally {
      // Clear all cached queries
      queryClient.clear();
      storeLogout();
    }
  }, [storeLogout, queryClient]);

  const switchCompany = useCallback(
    async (companyId: string) => {
      const response = await api.post<SwitchCompanyResponse>(
        "/v1/auth/switch-company",
        { companyId },
      );

      setTokens(response.accessToken, response.refreshToken);
      setCurrentCompany(companyId);
      setPermissions(response.permissions);

      // Invalidate all company-dependent queries to force refetch with new company context
      // This ensures all data is filtered by the new company
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          // Invalidate all queries except the companies list itself
          // since that's needed for the company switcher
          return key !== "companies";
        },
      });
    },
    [setTokens, setCurrentCompany, setPermissions, queryClient],
  );

  const updateLanguage = useCallback(
    async (language: LanguageCode | null) => {
      const response = await api.patch<{ preferredLanguage: LanguageCode | null }>(
        "/v1/users/me/language",
        { preferredLanguage: language },
      );

      setPreferredLanguage(response.preferredLanguage);
      // Re-initialize language with updated preference
      initializeLanguage(response.preferredLanguage, user?.companyDefaultLanguage);
    },
    [setPreferredLanguage, user?.companyDefaultLanguage],
  );

  const updateProfile = useCallback(
    async (data: ProfileUpdateData) => {
      const response = await api.patch<ProfileUpdateResponse>("/v1/users/me", data);

      // Update the user in the auth store with new profile data
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        const newName = [response.firstName, response.lastName].filter(Boolean).join(" ");
        const updatedUser = {
          ...currentUser,
          email: response.email,
          name: newName,
          firstName: response.firstName,
          lastName: response.lastName,
          displayName: response.displayName,
          namePrefix: response.namePrefix,
          pronouns: response.pronouns,
          preferredLanguage: response.preferredLanguage,
          phone: response.phone,
          documentNumber: response.documentNumber,
          jobTitle: response.jobTitle,
          department: response.department,
          timezone: response.timezone,
          sidebarStyle: response.sidebarStyle,
        };
        setUser(updatedUser);
        syncSidebarFromUser(updatedUser);

        // Re-initialize language if it changed
        if (response.preferredLanguage !== currentUser.preferredLanguage) {
          initializeLanguage(response.preferredLanguage, currentUser.companyDefaultLanguage);
        }
      }
    },
    [setUser],
  );

  const startImpersonation = useCallback(
    async (userId: string) => {
      const response = await api.post<ImpersonateUserResponse>(
        "/v1/auth/impersonate",
        { userId },
      );

      storeStartImpersonation(response);

      // Initialize language based on impersonated user preferences
      initializeLanguage(response.user.preferredLanguage, response.user.companyDefaultLanguage);

      // Invalidate all queries to force refetch with impersonated user context
      await queryClient.invalidateQueries();
    },
    [storeStartImpersonation, queryClient],
  );

  const endImpersonation = useCallback(async () => {
    const response = await api.post<EndImpersonationResponse>(
      "/v1/auth/end-impersonation",
    );

    storeEndImpersonation(response);

    // Initialize language based on original user preferences
    initializeLanguage(response.user.preferredLanguage, response.user.companyDefaultLanguage);

    // Invalidate all queries to force refetch with original user context
    await queryClient.invalidateQueries();
  }, [storeEndImpersonation, queryClient]);

  const clearForcedAction = useCallback(() => {
    setForcedAction(null);
  }, [setForcedAction]);

  const dismissMfaEnforcement = useCallback(() => {
    setMfaEnforcement(false, null);
  }, [setMfaEnforcement]);

  return {
    user,
    isAuthenticated,
    isLoading,
    currentCompanyId: user?.currentCompanyId ?? null,
    permissions: user?.permissions ?? [],
    isImpersonating,
    impersonator,
    forcedAction,
    mfaEnforcementWarning,
    mfaEnforcementDeadline,
    login,
    logout,
    switchCompany,
    updateLanguage,
    updateProfile,
    startImpersonation,
    endImpersonation,
    clearForcedAction,
    dismissMfaEnforcement,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}

export default useAuth;
